import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  ShoppingCart, Plus, Minus, X, Search, Package, MapPin, Truck,
  MessageCircle, Clock, Menu, Filter, ChevronDown, Info,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (n) => `₡${Number(n || 0).toLocaleString()}`;

const DAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function FacetGroup({ title, options, selected, onToggle }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-slate-200 py-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-sm font-bold uppercase text-slate-700 tracking-wide"
      >
        <span>{title}</span>
        <ChevronDown className={`w-4 h-4 transition ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-1.5 max-h-60 overflow-y-auto pr-1">
          {options.length === 0 && <p className="text-xs text-slate-400">—</p>}
          {options.map((opt) => {
            const isSel = selected.includes(opt.value);
            return (
              <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                <input
                  type="checkbox"
                  checked={isSel}
                  onChange={() => onToggle(opt.value)}
                  className="rounded accent-emerald-600"
                  data-testid={`facet-${title.toLowerCase()}-${opt.value}`}
                />
                <span className="flex-1 truncate">{opt.value}</span>
                <span className="text-xs text-slate-400">{opt.count}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function PublicStore() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [facets, setFacets] = useState({ categories: [], tallas: [], colors: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selCats, setSelCats] = useState([]);
  const [selTallas, setSelTallas] = useState([]);
  const [selColors, setSelColors] = useState([]);
  const [onlyInStock, setOnlyInStock] = useState(false);
  const [cart, setCart] = useState({});
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [paymentBanner, setPaymentBanner] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [enlargedImage, setEnlargedImage] = useState(null);

  // Customer accounts (Phase 2B)
  const tokenKey = `palistore_customer_token_${slug}`;
  const [customerToken, setCustomerToken] = useState(() => {
    try { return localStorage.getItem(tokenKey) || null; } catch (_) { return null; }
  });
  const [customer, setCustomer] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login"); // "login" | "register"
  const [authForm, setAuthForm] = useState({ name: "", phone: "", email: "", password: "", default_address: "" });
  const [showMyOrders, setShowMyOrders] = useState(false);
  const [myOrders, setMyOrders] = useState([]);

  const [checkout, setCheckout] = useState({
    customer_name: "", customer_phone: "", customer_email: "",
    delivery_method: "pickup", delivery_address: "",
    payment_intent: "pending", notes: "",
  });

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  // Stripe return polling
  useEffect(() => {
    const url = new URL(window.location.href);
    const sid = url.searchParams.get("session_id");
    const cancelled = url.searchParams.get("payment") === "cancelled";
    if (cancelled) {
      setPaymentBanner({ kind: "cancelled", message: "Pago cancelado. Tu pedido sigue pendiente." });
      url.searchParams.delete("payment");
      window.history.replaceState({}, "", url.pathname);
      return;
    }
    if (!sid) return;
    setPaymentBanner({ kind: "checking", message: "Confirmando pago..." });
    let n = 0;
    const iv = setInterval(async () => {
      n += 1;
      try {
        const r = await axios.get(`${API}/public/store/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          clearInterval(iv);
          setPaymentBanner({ kind: "paid", message: "¡Pago confirmado! Tu pedido fue procesado." });
          setCart({});
          url.searchParams.delete("session_id");
          window.history.replaceState({}, "", url.pathname);
        } else if (r.data.status === "expired" || n >= 8) {
          clearInterval(iv);
        }
      } catch { if (n >= 8) clearInterval(iv); }
    }, 2000);
    return () => clearInterval(iv);
  }, []);

  const fetchStore = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/store/${slug}`);
      setStore(r.data);
    } catch (e) {
      setError(e.response?.status === 404 ? "Tienda no encontrada o no activa." : "Error cargando la tienda.");
    }
  }, [slug]);

  const fetchProducts = useCallback(async () => {
    try {
      const params = {};
      if (search) params.q = search;
      if (selCats.length) params.category = selCats.join(",");
      if (selTallas.length) params.talla = selTallas.join(",");
      if (selColors.length) params.color = selColors.join(",");
      if (onlyInStock) params.in_stock = "true";
      const r = await axios.get(`${API}/public/store/${slug}/products`, { params });
      setProducts(r.data.products || []);
      setFacets(r.data.facets || { categories: [], tallas: [], colors: [] });
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, [slug, search, selCats, selTallas, selColors, onlyInStock]);

  useEffect(() => { fetchStore(); }, [fetchStore]);
  useEffect(() => {
    const t = setTimeout(fetchProducts, 300);
    return () => clearTimeout(t);
  }, [fetchProducts]);

  // Customer auth helpers (Phase 2B)
  const customerAxios = useCallback((extra = {}) => {
    const headers = { ...(extra.headers || {}) };
    if (customerToken) headers["X-Customer-Token"] = customerToken;
    return { ...extra, headers };
  }, [customerToken]);

  const fetchMe = useCallback(async () => {
    if (!customerToken) return;
    try {
      const r = await axios.get(`${API}/public/store/${slug}/me`, customerAxios());
      setCustomer(r.data.customer);
      setCheckout((c) => ({
        ...c,
        customer_name: c.customer_name || r.data.customer.name || "",
        customer_phone: c.customer_phone || r.data.customer.phone || "",
        customer_email: c.customer_email || r.data.customer.email || "",
        delivery_address: c.delivery_address || r.data.customer.default_address || "",
      }));
    } catch (_) {
      // Token invalid → clear
      setCustomerToken(null);
      try { localStorage.removeItem(tokenKey); } catch (e) { /* noop */ }
      setCustomer(null);
    }
  }, [customerToken, slug, customerAxios, tokenKey]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const doLogin = async () => {
    if (!authForm.phone || !authForm.password) { toast.error("Teléfono y contraseña requeridos"); return; }
    try {
      const r = await axios.post(`${API}/public/store/${slug}/auth/login`,
        { phone: authForm.phone, password: authForm.password });
      try { localStorage.setItem(tokenKey, r.data.token); } catch (_) { /* noop */ }
      setCustomerToken(r.data.token);
      setCustomer(r.data.customer);
      setShowAuth(false);
      setAuthForm({ name: "", phone: "", email: "", password: "", default_address: "" });
      toast.success(`¡Bienvenido, ${r.data.customer.name}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar sesión");
    }
  };

  const doRegister = async () => {
    if (!authForm.name || !authForm.phone || !authForm.password) {
      toast.error("Nombre, teléfono y contraseña requeridos"); return;
    }
    try {
      const r = await axios.post(`${API}/public/store/${slug}/auth/register`, {
        name: authForm.name, phone: authForm.phone, email: authForm.email,
        password: authForm.password, default_address: authForm.default_address,
      });
      try { localStorage.setItem(tokenKey, r.data.token); } catch (_) { /* noop */ }
      setCustomerToken(r.data.token);
      setCustomer(r.data.customer);
      setShowAuth(false);
      setAuthForm({ name: "", phone: "", email: "", password: "", default_address: "" });
      toast.success(`¡Cuenta creada! Hola, ${r.data.customer.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear cuenta");
    }
  };

  const doLogout = () => {
    setCustomerToken(null);
    setCustomer(null);
    try { localStorage.removeItem(tokenKey); } catch (_) { /* noop */ }
    toast.info("Sesión cerrada");
  };

  const fetchMyOrders = async () => {
    try {
      const r = await axios.get(`${API}/public/store/${slug}/me/orders`, customerAxios());
      setMyOrders(r.data.orders || []);
    } catch (_) { toast.error("Error cargando pedidos"); }
  };

  const doReorder = async (orderId) => {
    try {
      const r = await axios.post(`${API}/public/store/${slug}/me/reorder/${orderId}`, {}, customerAxios());
      const items = r.data.items || [];
      const skipped = r.data.skipped || [];
      if (items.length === 0) {
        toast.error("Ninguno de los productos del pedido está disponible ahora.");
        return;
      }
      const newCart = {};
      items.forEach((it) => {
        newCart[it.product_id] = {
          product: {
            id: it.product_id, code: it.code, name: it.name,
            color: it.color, talla: it.talla, price: it.price,
            image_url: it.image_url, quantity: 999,  // visual placeholder
          },
          quantity: it.quantity,
        };
      });
      setCart(newCart);
      setShowMyOrders(false);
      setShowCart(true);
      if (skipped.length) {
        toast.warning(`Algunos productos no se agregaron: ${skipped.map((s) => s.name).join(", ")}`);
      } else {
        toast.success("Productos agregados al carrito");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al recomprar");
    }
  };

  const themeColor = store?.theme_color || "#0F172A";
  const accentColor = store?.accent_color || "#10B981";

  const addToCart = (p) => {
    setCart((prev) => {
      const cur = prev[p.id]?.quantity || 0;
      if (cur + 1 > p.quantity) { toast.error(`Solo ${p.quantity} disponibles`); return prev; }
      return { ...prev, [p.id]: { product: p, quantity: cur + 1 } };
    });
  };
  const decreaseCart = (pid) => {
    setCart((prev) => {
      const cur = prev[pid]?.quantity || 0;
      if (cur <= 1) { const { [pid]: _, ...rest } = prev; return rest; }
      return { ...prev, [pid]: { ...prev[pid], quantity: cur - 1 } };
    });
  };
  const removeFromCart = (pid) => setCart((prev) => { const { [pid]: _, ...rest } = prev; return rest; });

  const cartItems = Object.values(cart);
  const cartCount = cartItems.reduce((a, i) => a + i.quantity, 0);
  const cartTotal = cartItems.reduce((a, i) => a + i.product.price * i.quantity, 0);

  const submitOrder = async () => {
    if (!checkout.customer_name.trim() || !checkout.customer_phone.trim()) { toast.error("Nombre y teléfono son requeridos"); return; }
    if (checkout.delivery_method === "delivery" && !checkout.delivery_address.trim()) { toast.error("Dirección requerida"); return; }
    setSubmitting(true);
    try {
      const payload = {
        box_id: store.box_id,
        customer_name: checkout.customer_name.trim(),
        customer_phone: checkout.customer_phone.trim(),
        customer_email: checkout.customer_email.trim() || null,
        delivery_method: checkout.delivery_method,
        delivery_address: checkout.delivery_method === "delivery" ? checkout.delivery_address.trim() : null,
        payment_intent: checkout.payment_intent,
        notes: checkout.notes.trim() || null,
        items: cartItems.map((i) => ({
          product_id: i.product.id, product_code: i.product.code, product_name: i.product.name,
          color: i.product.color, talla: i.product.talla,
          quantity: i.quantity, price: i.product.price, subtotal: i.product.price * i.quantity,
        })),
        subtotal: cartTotal, total: cartTotal,
      };
      const r = await axios.post(`${API}/public/store/${slug}/orders`, payload, customerAxios());
      const newOrder = r.data.order;
      if (checkout.payment_intent === "card") {
        try {
          const pay = await axios.post(`${API}/public/store/${slug}/orders/${newOrder.id}/pay`, { origin_url: window.location.origin });
          window.location.href = pay.data.url;
          return;
        } catch (e) { toast.error(e.response?.data?.detail || "Pago no disponible"); }
      }
      setOrderConfirmed(newOrder);
      setShowCheckout(false); setShowCart(false); setCart({});
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar pedido"); } finally { setSubmitting(false); }
  };

  const whatsappLink = useMemo(() => {
    const phone = (store?.whatsapp || "").replace(/\D/g, "");
    if (!phone) return null;
    const msg = encodeURIComponent(`Hola! Quería consultarles algo sobre la tienda *${store?.title || ""}*.`);
    return `https://wa.me/${phone}?text=${msg}`;
  }, [store]);

  const buildOrderWhatsApp = (order) => {
    const lines = [
      `🛒 *PEDIDO* ${order.order_number}`,
      `Cliente: ${order.customer_name}`, `Tel: ${order.customer_phone}`,
      order.customer_email ? `Email: ${order.customer_email}` : null,
      `Entrega: ${order.delivery_method === "delivery" ? "🚚 Envío" : "🏪 Retiro"}`,
      order.delivery_address ? `Dirección: ${order.delivery_address}` : null,
      `Pago: ${order.payment_intent}`, "",
      "*Productos:*",
      ...order.items.map((i) => `- ${i.product_name}${i.talla ? ` T:${i.talla}` : ""} x${i.quantity} = ${fmt(i.subtotal)}`),
      "", `*TOTAL: ${fmt(order.total)}*`,
      order.notes ? `\nNotas: ${order.notes}` : null,
    ].filter(Boolean);
    return lines.join("\n");
  };
  const sendOrderWhatsApp = (order) => {
    const phone = (store?.whatsapp || "").replace(/\D/g, "");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(buildOrderWhatsApp(order))}`, "_blank");
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <div className="bg-white rounded-lg p-8 max-w-md text-center shadow"><Package className="mx-auto w-12 h-12 text-slate-400 mb-3" /><h1 className="text-xl font-bold text-slate-700">{error}</h1></div>
    </div>
  );
  if (loading || !store) return <div className="min-h-screen flex items-center justify-center">Cargando tienda...</div>;

  // --- Sidebar (reusable for desktop + mobile) ---
  const Sidebar = (
    <aside className="w-full lg:w-64 lg:flex-shrink-0">
      <div className="space-y-1">
        <label className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded border bg-white">
          <input type="checkbox" checked={onlyInStock} onChange={(e) => setOnlyInStock(e.target.checked)} className="rounded accent-emerald-600" />
          <span className="font-semibold">Solo disponible en tienda (stock)</span>
        </label>
      </div>
      <FacetGroup title="CATEGORÍA" options={facets.categories} selected={selCats} onToggle={(v) => toggle(selCats, setSelCats, v)} />
      <FacetGroup title="TALLA" options={facets.tallas} selected={selTallas} onToggle={(v) => toggle(selTallas, setSelTallas, v)} />
      <FacetGroup title="COLOR" options={facets.colors} selected={selColors} onToggle={(v) => toggle(selColors, setSelColors, v)} />
      {(selCats.length + selTallas.length + selColors.length > 0 || onlyInStock) && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setSelCats([]); setSelTallas([]); setSelColors([]); setOnlyInStock(false); }}
          className="mt-3 w-full"
        >Limpiar filtros</Button>
      )}
    </aside>
  );

  return (
    <div
      className="min-h-screen"
      translate="no"
      style={{
        backgroundColor: "#F1F5F9",
        backgroundImage: store.background_url ? `url(${store.background_url})` : undefined,
        backgroundSize: "cover", backgroundAttachment: "fixed",
      }}
    >
      {/* Header */}
      <header style={{ backgroundColor: themeColor }} className="text-white sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          {store.logo_url ? (
            <img src={store.logo_url} alt={store.title} className="h-12 sm:h-14" />
          ) : (
            <h1 className="text-lg sm:text-2xl font-extrabold tracking-tight" data-testid="store-title">{store.title}</h1>
          )}
          <div className="hidden md:flex flex-1 max-w-md mx-auto">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Buscar productos..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white text-slate-900" data-testid="search-input" />
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Sheet open={showSidebar} onOpenChange={setShowSidebar}>
              <SheetTrigger asChild>
                <Button size="sm" variant="ghost" className="lg:hidden text-white"><Filter className="w-4 h-4" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 overflow-y-auto">{Sidebar}</SheetContent>
            </Sheet>
            {customer ? (
              <div className="relative inline-flex items-center gap-2">
                <Button
                  data-testid="open-my-account-btn"
                  onClick={() => { setShowMyOrders(true); fetchMyOrders(); }}
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
                  title={`Sesión: ${customer.name}`}
                >
                  <span className="hidden sm:inline mr-1">Hola,</span> {customer.name.split(" ")[0]}
                </Button>
              </div>
            ) : (
              <Button
                data-testid="open-auth-btn"
                onClick={() => { setAuthMode("login"); setShowAuth(true); }}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
              >
                <span className="hidden sm:inline">Mi cuenta</span>
                <span className="sm:hidden">Cuenta</span>
              </Button>
            )}
            <Button data-testid="open-cart-btn" onClick={() => setShowCart(true)} className="relative bg-white/10 hover:bg-white/20 text-white border border-white/20">
              <ShoppingCart className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Carrito</span>
              {cartCount > 0 && <span className="absolute -top-2 -right-2 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" style={{ backgroundColor: accentColor }}>{cartCount}</span>}
            </Button>
          </div>
        </div>
        {/* Mobile search */}
        <div className="md:hidden max-w-7xl mx-auto px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Buscar productos..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white text-slate-900" />
          </div>
        </div>
        {/* Hours bar */}
        {(store.hours_text || store.is_open !== null) && (
          <div className="bg-black/20 text-xs sm:text-sm py-1.5 px-4 flex items-center justify-center gap-2 flex-wrap">
            {store.is_open === true && <span className="bg-emerald-500 text-white px-2 py-0.5 rounded-full font-bold animate-pulse">🟢 ABIERTO</span>}
            {store.is_open === false && <span className="bg-red-500 text-white px-2 py-0.5 rounded-full font-bold">🔴 CERRADO</span>}
            {store.hours_text && <span className="flex items-center gap-1 text-slate-200"><Clock className="w-3 h-3" /> {store.hours_text}</span>}
          </div>
        )}
      </header>

      {/* Hero / Promo Banner */}
      {store.hero_message && (
        <div style={{ backgroundColor: accentColor }} className="text-white text-center py-3 px-4 font-bold text-lg shadow">
          {store.hero_message}
        </div>
      )}

      {/* Payment banner */}
      {paymentBanner && (
        <div data-testid="payment-banner" className={`px-4 py-3 text-center text-sm font-semibold ${
          paymentBanner.kind === "paid" ? "bg-emerald-600 text-white" :
          paymentBanner.kind === "checking" ? "bg-amber-500 text-white" :
          paymentBanner.kind === "cancelled" ? "bg-slate-700 text-white" : "bg-red-600 text-white"}`}>
          {paymentBanner.message}
          <button onClick={() => setPaymentBanner(null)} className="ml-3 underline text-xs">cerrar</button>
        </div>
      )}

      {/* Main: sidebar + product grid */}
      <main className="max-w-7xl mx-auto p-3 sm:p-4 flex gap-4">
        <div className="hidden lg:block">{Sidebar}</div>
        <div className="flex-1 min-w-0">
          <div className="bg-white rounded-lg shadow-sm px-4 py-2 mb-3 flex items-center justify-between text-sm">
            <span className="font-semibold text-slate-700">{products.length} producto{products.length !== 1 ? "s" : ""}</span>
            <div className="text-xs text-slate-500">
              {(selCats.length + selTallas.length + selColors.length) > 0 && `${selCats.length + selTallas.length + selColors.length} filtro(s) activo(s)`}
            </div>
          </div>
          {products.length === 0 ? (
            <div className="text-center py-16 text-slate-500 bg-white rounded-lg">No hay productos con esos filtros.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {products.map((p) => (
                <div
                  key={p.id}
                  data-testid={`product-card-${p.id}`}
                  className="bg-white rounded-lg overflow-hidden shadow hover:shadow-lg transition flex flex-col cursor-pointer group"
                  onClick={() => setProductDetail(p)}
                >
                  <div className="relative w-full overflow-hidden bg-slate-100" style={{ paddingTop: "100%" }}>
                    {p.image_url ? (
                      <img
                        src={p.image_url.startsWith("http") ? p.image_url : `${BACKEND_URL}${p.image_url}`}
                        alt={p.name}
                        className="group-hover:scale-105 transition"
                        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <div className="flex items-center justify-center" style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}>
                        <Package className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                    {/* Status badge */}
                    <div className="absolute top-2 left-2 z-10">
                      {p.available
                        ? <span className="bg-emerald-500 text-white text-xs font-extrabold px-2 py-1 rounded shadow">EN TIENDA</span>
                        : <span className="bg-amber-500 text-white text-xs font-extrabold px-2 py-1 rounded shadow">ENCARGAR</span>}
                    </div>
                  </div>
                  <div className="p-3 flex flex-col gap-1 flex-1">
                    <div className="text-[10px] font-mono text-slate-400">{p.code}</div>
                    <h3 className="font-semibold text-sm leading-tight line-clamp-2 min-h-[2.5rem]">{p.name}</h3>
                    <div className="flex gap-1 flex-wrap">
                      {p.color && <span className="text-[10px] bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded">{p.color}</span>}
                      {p.talla && <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">T:{p.talla}</span>}
                    </div>
                    <div className="text-xl font-extrabold mt-auto" style={{ color: accentColor }}>{fmt(p.price)}</div>
                    <button
                      type="button"
                      data-testid={`product-features-btn-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); setProductDetail(p); }}
                      className="mt-1 inline-flex items-center justify-center gap-1 text-[11px] font-semibold py-1 px-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 transition"
                    >
                      <Info className="w-3 h-3" />
                      Características
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* WhatsApp floating bubble */}
      {whatsappLink && (
        <a
          href={whatsappLink}
          target="_blank"
          rel="noreferrer"
          data-testid="whatsapp-bubble"
          className="fixed bottom-5 right-5 z-50 rounded-full shadow-2xl bg-green-500 hover:bg-green-600 text-white p-4 transition hover:scale-110 flex items-center gap-2"
          title="Chatea con nosotros"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="hidden sm:inline font-bold pr-1">WhatsApp</span>
        </a>
      )}

      {/* Product detail dialog */}
      <Dialog open={!!productDetail} onOpenChange={(o) => !o && setProductDetail(null)}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto">
          {productDetail && (
            <>
              <DialogHeader>
                <DialogTitle>{productDetail.name}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left column: images */}
                <div className="space-y-3">
                  <div className="relative w-full overflow-hidden bg-slate-100 rounded cursor-zoom-in" style={{ paddingTop: "100%" }}
                       onClick={() => productDetail.image_url && setEnlargedImage(productDetail.image_url.startsWith("http") ? productDetail.image_url : `${BACKEND_URL}${productDetail.image_url}`)}
                       data-testid="zoom-main-image"
                       title="Click para ampliar">
                    {productDetail.image_url ? (
                      <img
                        src={productDetail.image_url.startsWith("http") ? productDetail.image_url : `${BACKEND_URL}${productDetail.image_url}`}
                        alt={productDetail.name}
                        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <div className="flex items-center justify-center" style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}>
                        <Package className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                  </div>
                  {productDetail.spec_image_url && (
                    <div className="rounded-md border border-slate-200 overflow-hidden cursor-zoom-in relative group" data-testid="detail-spec-image"
                         onClick={() => setEnlargedImage(productDetail.spec_image_url.startsWith("http") ? productDetail.spec_image_url : `${API.replace(/\/api$/, "")}${productDetail.spec_image_url}`)}
                         title="Click para ampliar las características">
                      <img
                        src={productDetail.spec_image_url.startsWith("http") ? productDetail.spec_image_url : `${API.replace(/\/api$/, "")}${productDetail.spec_image_url}`}
                        alt="Características"
                        className="w-full h-auto object-contain bg-white"
                      />
                      <div className="absolute top-1 right-1 bg-black/60 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none">
                        🔍 Click para ampliar
                      </div>
                    </div>
                  )}
                </div>
                {/* Right column: details */}
                <div className="space-y-2">
                  <div className="text-xs font-mono text-slate-400">{productDetail.code}</div>
                  <div className="flex gap-1 flex-wrap">
                    {productDetail.color && <span className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded">{productDetail.color}</span>}
                    {productDetail.talla && <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">Talla: {productDetail.talla}</span>}
                    {productDetail.category && <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{productDetail.category}</span>}
                  </div>
                  <div className="text-3xl font-extrabold" style={{ color: accentColor }}>{fmt(productDetail.price)}</div>
                  {productDetail.description && <p className="text-sm text-slate-600">{productDetail.description}</p>}
                  {productDetail.composition && (
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="detail-composition">
                      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Composición / Materiales</div>
                      <div className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{productDetail.composition}</div>
                    </div>
                  )}
                  {productDetail.master_comments && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-2" data-testid="detail-master-comments">
                      <div className="text-[11px] font-bold uppercase tracking-wide text-amber-700 flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        Notas de la tienda
                      </div>
                      <div className="text-sm text-amber-900 mt-0.5 whitespace-pre-wrap">{productDetail.master_comments}</div>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                {productDetail.available ? (
                  <Button onClick={() => { addToCart(productDetail); setProductDetail(null); }} className="w-full" style={{ backgroundColor: accentColor }} data-testid="detail-add-cart">
                    <Plus className="w-4 h-4 mr-1" /> Agregar al carrito
                  </Button>
                ) : (
                  <Button disabled className="w-full bg-amber-500">ENCARGAR — Consulta por WhatsApp</Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Cart Dialog (unchanged from v1 mostly) */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>Tu carrito ({cartCount})</DialogTitle></DialogHeader>
          {cartItems.length === 0 ? (
            <div className="text-center py-8 text-slate-500">Tu carrito está vacío.</div>
          ) : (
            <>
              <div className="overflow-y-auto flex-1 space-y-2 my-2">
                {cartItems.map((item) => (
                  <div key={item.product.id} className="flex items-center gap-2 border rounded-md p-2">
                    {item.product.image_url ? (
                      <img src={item.product.image_url.startsWith("http") ? item.product.image_url : `${BACKEND_URL}${item.product.image_url}`} alt="" className="w-12 h-12 rounded object-cover" />
                    ) : <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center"><Package className="w-5 h-5 text-slate-400" /></div>}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-mono text-slate-400">{item.product.code}</div>
                      <div className="text-sm font-semibold truncate">{item.product.name}</div>
                      <div className="text-xs text-slate-500">{fmt(item.product.price)} c/u</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => decreaseCart(item.product.id)}><Minus className="w-3 h-3" /></Button>
                      <span className="font-bold w-6 text-center">{item.quantity}</span>
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => addToCart(item.product)}><Plus className="w-3 h-3" /></Button>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeFromCart(item.product.id)}><X className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
              <div className="border-t pt-2 flex items-center justify-between font-bold text-lg"><span>Total:</span><span style={{ color: accentColor }}>{fmt(cartTotal)}</span></div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCart(false)}>Seguir comprando</Button>
            {cartItems.length > 0 && <Button onClick={() => { setShowCart(false); setShowCheckout(true); }} style={{ backgroundColor: accentColor }} data-testid="proceed-checkout-btn">Hacer pedido</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checkout */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Datos del pedido</DialogTitle>
            <DialogDescription>Total: <strong style={{ color: accentColor }}>{fmt(cartTotal)}</strong></DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre completo *</Label><Input data-testid="checkout-name" value={checkout.customer_name} onChange={(e) => setCheckout({ ...checkout, customer_name: e.target.value })} /></div>
            <div><Label>Teléfono *</Label><Input data-testid="checkout-phone" value={checkout.customer_phone} onChange={(e) => setCheckout({ ...checkout, customer_phone: e.target.value })} /></div>
            <div><Label>Email (opcional)</Label><Input value={checkout.customer_email} onChange={(e) => setCheckout({ ...checkout, customer_email: e.target.value })} /></div>
            <div>
              <Label>¿Cómo prefieres recibirlo?</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Button type="button" variant={checkout.delivery_method === "pickup" ? "default" : "outline"} onClick={() => setCheckout({ ...checkout, delivery_method: "pickup" })}><MapPin className="w-4 h-4 mr-1" /> Retiro</Button>
                <Button type="button" variant={checkout.delivery_method === "delivery" ? "default" : "outline"} onClick={() => setCheckout({ ...checkout, delivery_method: "delivery" })}><Truck className="w-4 h-4 mr-1" /> Envío</Button>
              </div>
            </div>
            {checkout.delivery_method === "delivery" && <div><Label>Dirección *</Label><Textarea rows={2} value={checkout.delivery_address} onChange={(e) => setCheckout({ ...checkout, delivery_address: e.target.value })} /></div>}
            <div>
              <Label>Forma de pago</Label>
              <Select value={checkout.payment_intent} onValueChange={(v) => setCheckout({ ...checkout, payment_intent: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Por confirmar con tienda</SelectItem>
                  <SelectItem value="sinpe">SINPE Móvil</SelectItem>
                  <SelectItem value="cash_on_pickup">Efectivo al recibir</SelectItem>
                  <SelectItem value="card">💳 Pagar con tarjeta (Stripe)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notas (opcional)</Label><Textarea rows={2} value={checkout.notes} onChange={(e) => setCheckout({ ...checkout, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckout(false)}>Cancelar</Button>
            <Button onClick={submitOrder} disabled={submitting} style={{ backgroundColor: accentColor }} data-testid="submit-order-btn">{submitting ? "Enviando..." : "Enviar pedido"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order confirmation */}
      <Dialog open={!!orderConfirmed} onOpenChange={(o) => !o && setOrderConfirmed(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-emerald-600">¡Pedido recibido!</DialogTitle>
            <DialogDescription>Tu pedido <strong>{orderConfirmed?.order_number}</strong> fue registrado.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            {store.whatsapp && orderConfirmed && (
              <Button onClick={() => sendOrderWhatsApp(orderConfirmed)} className="bg-green-600 hover:bg-green-700" data-testid="send-whatsapp-btn">
                <MessageCircle className="w-4 h-4 mr-1" /> Enviar por WhatsApp
              </Button>
            )}
            <Button variant="outline" onClick={() => setOrderConfirmed(null)}>Listo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image lightbox — click on any product image to view it full-screen */}
      <Dialog open={!!enlargedImage} onOpenChange={(o) => !o && setEnlargedImage(null)}>
        <DialogContent
          className="max-w-[95vw] max-h-[95vh] p-2 bg-black/90 border-0"
          data-testid="image-lightbox"
          onClick={() => setEnlargedImage(null)}
        >
          {enlargedImage && (
            <img
              src={enlargedImage}
              alt="Vista ampliada"
              className="w-full h-auto max-h-[90vh] object-contain mx-auto cursor-zoom-out"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Auth modal (login / register) */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="max-w-md" data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle>{authMode === "login" ? "Iniciar sesión" : "Crear cuenta"}</DialogTitle>
            <DialogDescription>
              {authMode === "login"
                ? "Accede para ver tus pedidos y comprar más rápido"
                : "Crea tu cuenta para tener tu historial y datos de envío guardados"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {authMode === "register" && (
              <div>
                <Label htmlFor="auth-name">Nombre completo</Label>
                <Input id="auth-name" data-testid="auth-name-input" value={authForm.name} onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })} />
              </div>
            )}
            <div>
              <Label htmlFor="auth-phone">Teléfono</Label>
              <Input id="auth-phone" data-testid="auth-phone-input" placeholder="8888-6666" value={authForm.phone} onChange={(e) => setAuthForm({ ...authForm, phone: e.target.value })} />
            </div>
            {authMode === "register" && (
              <>
                <div>
                  <Label htmlFor="auth-email">Correo (opcional)</Label>
                  <Input id="auth-email" data-testid="auth-email-input" type="email" value={authForm.email} onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="auth-address">Dirección de envío (opcional)</Label>
                  <Input id="auth-address" data-testid="auth-address-input" value={authForm.default_address} onChange={(e) => setAuthForm({ ...authForm, default_address: e.target.value })} placeholder="Para no escribirla en cada pedido" />
                </div>
              </>
            )}
            <div>
              <Label htmlFor="auth-pass">Contraseña</Label>
              <Input id="auth-pass" data-testid="auth-password-input" type="password" value={authForm.password} onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })} />
            </div>
          </div>
          <DialogFooter className="flex-col gap-2">
            <Button
              data-testid="auth-submit-btn"
              onClick={authMode === "login" ? doLogin : doRegister}
              className="w-full"
              style={{ backgroundColor: accentColor }}
            >
              {authMode === "login" ? "Entrar" : "Crear cuenta"}
            </Button>
            <button
              type="button"
              className="text-sm text-slate-600 hover:text-slate-900 mx-auto"
              onClick={() => setAuthMode(authMode === "login" ? "register" : "login")}
              data-testid="auth-toggle-mode"
            >
              {authMode === "login" ? "¿No tienes cuenta? Regístrate" : "¿Ya tienes cuenta? Inicia sesión"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "Mis pedidos" dialog (logged-in customers) */}
      <Dialog open={showMyOrders} onOpenChange={setShowMyOrders}>
        <DialogContent className="max-w-2xl" data-testid="my-orders-dialog">
          <DialogHeader>
            <DialogTitle>Mis pedidos · {customer?.name}</DialogTitle>
            <DialogDescription>
              Historial de tus pedidos. Pulsa “Volver a pedir” para llenar el carrito con los mismos productos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {myOrders.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-6">No tienes pedidos aún.</p>
            )}
            {myOrders.map((o) => {
              const stColor = o.status === "confirmed" ? "bg-emerald-100 text-emerald-800"
                : o.status === "cancelled" ? "bg-red-100 text-red-800"
                : "bg-amber-100 text-amber-800";
              return (
                <div key={o.id} className="rounded-md border border-slate-200 p-3" data-testid={`my-order-${o.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <div className="font-mono font-bold text-sm">{o.order_number}</div>
                      <div className="text-xs text-slate-500">{(o.created_at || "").substring(0, 16).replace("T", " ")}</div>
                      <div className="text-sm mt-1">
                        {(o.items || []).slice(0, 3).map((i) => i.product_name).join(", ")}
                        {(o.items || []).length > 3 && ` +${o.items.length - 3} más`}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${stColor}`}>
                        {o.status === "confirmed" ? "Entregado" : o.status === "cancelled" ? "Cancelado" : "En proceso"}
                      </span>
                      <div className="font-bold text-sm mt-1">{fmt(o.final_total ?? o.total)}</div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 w-full"
                    onClick={() => doReorder(o.id)}
                    data-testid={`reorder-btn-${o.id}`}
                  >
                    Volver a pedir
                  </Button>
                </div>
              );
            })}
          </div>
          <DialogFooter className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => { doLogout(); setShowMyOrders(false); }}
              data-testid="logout-btn"
              className="border-red-300 text-red-700"
            >
              Cerrar sesión
            </Button>
            <Button variant="outline" onClick={() => setShowMyOrders(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <footer className="text-center text-xs text-slate-500 py-6">
        {store.whatsapp && <div>📱 {store.whatsapp}</div>}
        {store.hours_text && <div className="mt-1">{store.hours_text}</div>}
      </footer>
    </div>
  );
}
